import { useMemo, useState, type ReactNode } from 'react';

import { TableViewContext, type TableViewLayout } from './TableViewContext';

/**
 * Owns the Network Overview table's layout state (closed / split / maximised) for
 * the Find & Fix map. Mounted in the find-and-fix route layout, above the <Outlet>
 * that renders MapView, so the layout survives panel toggles — and later table UI
 * state (pagination, sort, expansion) can live here too, preserved across close.
 */
export function TableViewProvider({ children }: { children: ReactNode }) {
    const [layout, setLayout] = useState<TableViewLayout>('closed');

    const value = useMemo(
        () => ({
            layout,
            isOpen: layout !== 'closed',
            open: () => {
                setLayout((prev) => (prev === 'closed' ? 'split' : prev));
            },
            close: () => {
                setLayout('closed');
            },
            maximise: () => {
                setLayout('maximised');
            },
            restoreSplit: () => {
                setLayout('split');
            },
            toggle: () => {
                setLayout((prev) => (prev === 'closed' ? 'split' : 'closed'));
            },
        }),
        [layout],
    );

    return <TableViewContext value={value}>{children}</TableViewContext>;
}
